import $ from "jquery";
import commandHistory from "flow/cli/commandHistory";
import commandParser from "flow/cli/commandParser";
import commandRepository from "flow/cli/commandRepository";
import Writer from "./Writer";

let exitCallback = function () {};
let hideCallback = function () {};
let showCallback = function () {};

export interface ICommandResult {
    input: string;
}

function shortManual(commandName: string) {
    const command = commandRepository.getCommand(commandName);

    let manualLine = commandName;

    if (command.options.length > 0 || command.arguments.length > 0) {
        manualLine += " ";
    }

    if (command.options.length > 0) {
        manualLine += "[options] ";
    }

    $.each(command.arguments, function () {
        manualLine += this.name + " ";
    });

    return manualLine;
}

function execute(writer: Writer, _prompt: any, commandLine: string): JQueryPromise<ICommandResult> {
    const deferred = $.Deferred<ICommandResult>();

    writer.command.writeLine(commandLine);

    const commandName = commandParser.getCommandName(commandLine);

    const matchedCommand = commandRepository.getCommand(commandName);

    if (matchedCommand) {
        commandHistory.push(commandLine);

        let optsAndArgs;

        try {
            optsAndArgs = commandParser.parseOptsAndArgs(matchedCommand, commandLine);
        } catch (e) {
            writer.error.writeLine(e);

            deferred.resolve({ input: "" });
            return deferred.promise();
        }

        // Extra args
        optsAndArgs._commandHistory = commandHistory;
        optsAndArgs._commandRepository = commandRepository;
        optsAndArgs._exitCallback = exitCallback;
        optsAndArgs._hideCallback = hideCallback;
        optsAndArgs._showCallback = showCallback;

        $.when(matchedCommand.callback(writer, optsAndArgs)).then(function (result) {
            deferred.resolve(result);
        });
    } else if (commandLine === "") {
        deferred.resolve({ input: "" });
    } else {
        commandHistory.push(commandLine);
        writer.error.writeLine("There is no such command.");
        deferred.resolve({ input: "" });
    }

    return deferred.promise();
}

function hint(writer: Writer, commandLine: string): ICommandResult {
    const commandName = commandParser.getCommandName(commandLine);
    const candidates = commandRepository.getCommandsWhereNameStartsWith(commandLine);

    if (commandRepository.getCommand(commandName)) {
        if (commandLine.indexOf(" ") > 0 || candidates.length === 1) {
            writer.help.writeLine(shortManual(commandName));
            return { input: commandLine };
        }
    }

    const hints = candidates
        .map(c => c.name)
        .sort()
        .join(" ");
    writer.help.writeLine(hints);
    return { input: commandLine };
}

function complete(commandLine: string): ICommandResult {
    const matches = commandRepository.getCommandsWhereNameStartsWith(commandLine);

    if (matches.length === 0) {
        return { input: commandLine };
    }

    if (matches.length === 1) {
        return { input: matches[0].name };
    }

    if (commandLine.indexOf(" ") !== -1) {
        return { input: commandLine };
    }

    const names = matches.map(c => c.name).sort();

    const commonBeginning = getCommonBeginning(names);

    return { input: commonBeginning };
}

function getCommonBeginning(strings: string[]): string {
    const first = strings[0];
    const last = strings[strings.length - 1];
    let i;

    for (i = 0; i < first.length; ++i) {
        if (first.charAt(i) !== last.charAt(i)) {
            break;
        }
    }

    return first.substring(0, i);
}

function getPreviousCommand() {
    return commandHistory.getPreviousCommand();
}

function getNextCommand() {
    return commandHistory.getNextCommand();
}

function exitFunction(callback: () => void) {
    exitCallback = callback;
}

function hideFunction(callback: () => void) {
    hideCallback = callback;
}

function showFunction(callback: () => void) {
    showCallback = callback;
}

export default {
    execute: execute,
    hint: hint,
    complete: complete,
    getPreviousCommand: getPreviousCommand,
    getNextCommand: getNextCommand,
    exitFunction: exitFunction,
    hideFunction: hideFunction,
    showFunction: showFunction
};
